<div class="w-full relative" [class.inline-calendar]="inline">
  <div *ngIf="!inline" class="relative">
    <input type="text" [id]="displayId" [name]="displayName" [value]="displayValue"
      [placeholder]="displayLabel ? '' : displayPlaceholder" [required]="isRequired" [disabled]="isDisabled"
      [readonly]="isReadonly" (input)="onDateChange($event)" (keydown)="onKeyDown($event)" (keyup)="onKeyUp($event)" (paste)="onPaste($event)" (blur)="onDateBlur($event)" (focus)="onFocus($event)"
      (click)="toggleDropdown()" [class.border-red-500]="isRequired && isTouched && !hasValue"
      maxlength="10" inputmode="numeric"
      class="w-full px-3 py-2.5 text-[14px] border border-primary rounded-md text-primary bg-bg focus:outline-none transition-colors disabled:bg-surface disabled:cursor-not-allowed disabled:opacity-60 font-semibold h-[40px]" />
    <label *ngIf="displayLabel" [for]="displayId" [class.floating-label-active]="isFocused || hasValue || showDropdown"
      [class.text-red-500]="isRequired && !isLabelStandard" [class.text-muted]="!isRequired || isLabelStandard"
      class="floating-label absolute left-3 text-[13px] font-medium pointer-events-none transition-all duration-200">
      {{ displayLabel }}{{ isFocused && !hasValue ? ' (' + displayPlaceholder + ')' : '' }}
      <span *ngIf="isRequired" class="text-red-500 ml-1">*</span>
    </label>

    <div (click)="toggleDropdown()"
      class="absolute right-2.5 top-1/2 -translate-y-1/2 pointer-events-auto cursor-pointer z-10">
      <svg class="w-4 h-4 text-primary" fill="none" stroke="currentColor" viewBox="0 0 24 24"
        xmlns="http://www.w3.org/2000/svg">
        <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2"
          d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z"></path>
      </svg>
    </div>
  </div>

  <!-- Redesigned Custom Calendar Popup: mousedown preventDefault keeps focus on the input so blur/focusin
       does not fire document listeners that close the popup or break the second open/month navigation. -->
  <div *ngIf="showDropdown" [ngStyle]="inline ? {} : calendarStyle" [class.static-calendar]="inline"
    class="calendar-popup border border-border rounded-xl bg-bg overflow-hidden shadow-md"
    (mousedown)="$event.preventDefault()" (click)="$event.stopPropagation()">
    <div class="p-3.5">
      <!-- Elegant Header Navigation -->
      <div class="flex items-center justify-between mb-3 px-0.5">
        <div class="flex items-center gap-0.5">
          <button type="button" (click)="showMonths($event)"
            class="flex items-center gap-1 text-sm font-extrabold text-primary hover:bg-surface px-1.5 py-1 rounded-md transition-all active:scale-95 group">
            {{ months[viewDate.getMonth()] }}
            <svg class="w-2.5 h-2.5 text-primary/40 group-hover:text-primary/70 transition-transform duration-300"
              [class.rotate-180]="viewMode === 'months'" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="4" d="M19 9l-7 7-7-7" />
            </svg>
          </button>
          <button type="button" (click)="showYears($event)"
            class="flex items-center gap-1 text-sm font-extrabold text-primary hover:bg-surface px-1.5 py-1 rounded-md transition-all active:scale-95 group">
            {{ viewDate.getFullYear() }}
            <svg class="w-2.5 h-2.5 text-primary/40 group-hover:text-primary/70 transition-transform duration-300"
              [class.rotate-180]="viewMode === 'years'" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="4" d="M19 9l-7 7-7-7" />
            </svg>
          </button>
        </div>

        <div class="flex gap-0.5">
          <button type="button" (click)="onPrevClick($event)"
            class="p-1.5 hover:bg-surface rounded-full transition-all text-primary active:scale-90 opacity-70 hover:opacity-100">
            <svg class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="3" d="M15 19l-7-7 7-7" />
            </svg>
          </button>
          <button type="button" (click)="onNextClick($event)"
            class="p-1.5 hover:bg-surface rounded-full transition-all text-primary active:scale-90 opacity-70 hover:opacity-100">
            <svg class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="3" d="M9 5l7 7-7 7" />
            </svg>
          </button>
        </div>
      </div>

      <!-- Days View Mode -->
      <div *ngIf="viewMode === 'days'" class="animate-content">
        <!-- Muted Weekday Labels -->
        <div class="grid grid-cols-7 mb-1.5">
          <div *ngFor="let day of weekdays"
            class="text-center text-[10px] font-bold text-muted/30 uppercase tracking-widest py-0.5">
            {{ day }}
          </div>
        </div>

        <!-- Calendar Grid with Circular Highlights -->
        <div class="grid grid-cols-7 gap-y-0 gap-x-0">
          <div *ngFor="let day of days" (click)="!day.isDisabled && selectDate(day.date, $event)"
            [class.cursor-pointer]="!day.isDisabled"
            [class.cursor-not-allowed]="day.isDisabled"
            class="aspect-square flex items-center justify-center relative group rounded-full overflow-hidden"
            [class.opacity-50]="day.isDisabled">

            <!-- Selection Highlight (Solid Primary Circle) -->
            <div *ngIf="day.isSelected"
              class="absolute h-8 w-8 rounded-full bg-primary shadow-md ring-2 ring-primary ring-offset-bg select-pop">
            </div>

            <!-- Today Indicator (Clear Tint + Stronger Border) -->
            <div *ngIf="day.isToday && !day.isSelected"
              class="absolute h-8 w-8 rounded-full border-2 border-primary/40 bg-primary/5"></div>

            <!-- Smooth Hover Highlight (Visible on all non-selected dates) -->
            <div *ngIf="!day.isSelected && !day.isDisabled"
              class="absolute h-8 w-8 rounded-full bg-transparent group-hover:bg-primary/10 group-active:bg-primary/15 transition-all duration-200">
            </div>

            <span class="relative text-[12.5px] font-extrabold transition-all duration-300"
              [class.text-white]="day.isSelected"
              [class.text-primary]="!day.isSelected && (day.isCurrentMonth || day.isToday) && !day.isDisabled"
              [class.text-muted]="day.isDisabled"
              [class.opacity-25]="!day.isCurrentMonth && !day.isToday"
              [class.opacity-50]="day.isDisabled"
              [class.scale-110]="day.isSelected">
              {{ day.date.getDate() }}
            </span>
          </div>
        </div>
      </div>

      <!-- Months View Mode -->
      <div *ngIf="viewMode === 'months'" class="animate-content py-0.5">
        <div class="grid grid-cols-3 gap-1.5">
          <div *ngFor="let monthName of months; let i = index" (click)="selectMonth(i, $event)"
            class="px-1 py-3 text-center text-[11px] font-bold rounded-lg cursor-pointer transition-all hover:bg-primary/5 active:scale-95"
            [class.bg-primary]="viewDate.getMonth() === i" [class.text-white]="viewDate.getMonth() === i"
            [class.text-primary]="viewDate.getMonth() !== i">
            {{ monthName.toUpperCase().substring(0, 3) }}
          </div>
        </div>
      </div>

      <!-- Years View Mode (Scrollable) -->
      <div *ngIf="viewMode === 'years'" class="animate-content py-0.5">
        <div class="max-h-52 overflow-y-auto custom-scrollbar pr-1 -mr-1">
          <div class="grid grid-cols-3 gap-1.5">
            <div *ngFor="let yr of years" [id]="'year-' + yr" (click)="selectYear(yr, $event)"
              class="px-1 py-3 text-center text-[11px] font-bold rounded-lg cursor-pointer transition-all hover:bg-primary/5 active:scale-95"
              [class.bg-primary]="viewDate.getFullYear() === yr" [class.text-white]="viewDate.getFullYear() === yr"
              [class.text-primary]="viewDate.getFullYear() !== yr">
              {{ yr }}
            </div>
          </div>
        </div>
      </div>
    </div>

    <!-- Footer Actions -->
    <div class="border-t border-border p-2 px-4 flex justify-between bg-surface/30 backdrop-blur-sm">
      <button type="button" (click)="clearDate($event)"
        class="text-[10px] font-extrabold text-status-danger hover:opacity-70 transition-opacity uppercase tracking-wider">
        Clear
      </button>
      <button type="button" (click)="selectToday($event)"
        class="text-[10px] font-extrabold text-primary hover:opacity-70 transition-opacity uppercase tracking-wider">
        Today
      </button>
    </div>
  </div>
</div>